import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

/**
 * Before/After carousel: centered spotlight image with circular arrows and
 * a peek of surrounding thumbnails (mimicking the natural.clinic layout).
 */
const BeforeAfterCarousel = ({ title, treatmentLabel, images = [] }) => {
  const { t } = useLang();
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const m = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(m.matches);
    apply();
    m.addEventListener('change', apply);
    return () => m.removeEventListener('change', apply);
  }, []);

  const len = images.length;
  const safeMod = useCallback((n) => ((n % len) + len) % len, [len]);

  const next = useCallback(() => setIndex((i) => safeMod(i + 1)), [safeMod]);
  const prev = useCallback(() => setIndex((i) => safeMod(i - 1)), [safeMod]);

  // Build an ordered list of 9 images centered around the current index
  // so we can show peeks on both sides even with small galleries.
  const visible = useMemo(() => {
    if (!len) return [];
    return [-3, -2, -1, 0, 1, 2, 3].map((o) => ({
      src: images[safeMod(index + o)],
      offset: o,
    }));
  }, [images, index, len, safeMod]);

  if (!len) return null;

  return (
    <section className="relative overflow-hidden bg-[#0B3D2E] py-20 md:py-28" data-testid="before-after-carousel">
      {/* Soft glow */}
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-[#D4AF37]/5 blur-[140px]" />

      <div className="relative max-w-[1280px] mx-auto px-6 text-center">
        <h2 className="font-serif-display leading-[1.1] text-4xl md:text-5xl lg:text-6xl text-white">
          {t('gallery.title', null, { name: treatmentLabel })}
        </h2>
        <h3 className="font-serif-display italic text-3xl md:text-5xl lg:text-6xl mt-2 peach-text">
          {t('gallery.subtitle', null, { name: treatmentLabel })}
        </h3>
        <p className="mt-6 text-[15px] md:text-base text-white/65 max-w-xl mx-auto">
          {t('gallery.caption')}
        </p>
      </div>

      {/* Carousel stage */}
      <div className="relative mt-10 md:mt-16 h-[380px] sm:h-[440px] md:h-[500px] flex items-center justify-center overflow-hidden">
        {/* Filmstrip: all images absolutely positioned, centered one is the spotlight */}
        <div className="relative w-full h-full flex items-center justify-center">
          {visible.map(({ src, offset }, i) => {
            const isActive = offset === 0;
            const abs = Math.abs(offset);
            const sign = Math.sign(offset);
            const desktopSpacing = [0, 330, 570, 790, 990];
            const mobileSpacing = [0, 200, 360, 480, 580];
            const px = (isMobile ? mobileSpacing : desktopSpacing)[Math.min(abs, 4)] || 0;
            const translate = sign * px;
            const scale = isActive ? 1 : 0.78 - Math.min(abs - 1, 3) * 0.06;
            const opacity = isActive ? 1 : Math.max(0.3, 0.8 - abs * 0.18);
            // On mobile, hide ALL non-active thumbnails so the spotlight is clean and
            // never gets clipped by the viewport edges.
            const hideOnMobile = abs > 0;

            const activeSize = isMobile ? 320 : 460;
            const sideSize = isMobile ? 160 : 280;

            return (
              <div
                key={`${src}-${offset}-${i}`}
                className={`absolute top-1/2 left-1/2 transition-all duration-500 ease-out ${
                  hideOnMobile ? 'hidden md:block' : ''
                }`}
                style={{
                  transform: `translate(-50%, -50%) translateX(${translate}px) scale(${scale})`,
                  opacity,
                  zIndex: 20 - abs,
                }}
              >
                <div
                  className={`relative rounded-[6px] overflow-hidden bg-[#072A1F] shadow-[0_30px_70px_-15px_rgba(0,0,0,0.65)] ${
                    isActive ? 'ring-1 ring-[#D4AF37]/40' : ''
                  }`}
                  style={{
                    width: `${isActive ? activeSize : sideSize}px`,
                    height: `${isActive ? activeSize : sideSize}px`,
                  }}
                >
                  <img
                    src={src}
                    alt={`${treatmentLabel} before and after ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.currentTarget.style.opacity = '0.35'; }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Arrow controls */}
        <button
          type="button"
          aria-label="Previous"
          onClick={prev}
          data-testid="carousel-prev-btn"
          className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] hover:border-[#D4AF37] transition-all flex items-center justify-center"
        >
          <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={next}
          data-testid="carousel-next-btn"
          className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] hover:border-[#D4AF37] transition-all flex items-center justify-center"
        >
          <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </div>

      {/* Dots */}
      <div className="relative flex items-center justify-center gap-2 mt-10">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Slide ${i + 1}`}
            data-testid={`carousel-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? 'w-8 bg-[#D4AF37]' : 'w-2 bg-white/30 hover:bg-white/60'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default BeforeAfterCarousel;
